import { Navigate, useRoutes } from 'react-router-dom';

import DashboardLayout from '../layouts/dashboard';
import LogoOnlyLayout from '../layouts/LogoOnlyLayout';

import GuestGuard from '../guards/GuestGuard';
import AuthGuard from '../guards/AuthGuard';
import PermissionGuard from '../guards/PermissionGuard';

import { PATH_AFTER_LOGIN } from '../config';
import { PATH_DASHBOARD, PATH_GIFTCARD, PATH_PAGE } from './paths';

import Login from '../pages/auth/Login';
import Register from '../pages/auth/Register';
import ResetPassword from '../pages/auth/ResetPassword';
import AdminModulePage from '../pages/admin/AdminModulePage';
import Unauthorized from '../pages/Unauthorized';
import AppSelector from '../pages/AppSelector';
import CardRedemptionApp from '../pages/giftcard/CardRedemptionApp';

import AdminUserList from '../pages/dashboard/AdminUserList';
import CryptoList from '../pages/dashboard/CryptoList';
import CryptoExchangeRange from '../pages/dashboard/CryptoExchangeRange';
import FeaturedBlogPosts from '../pages/dashboard/FeaturedBlogPosts';
import AppPostContents from '../pages/dashboard/AppPostContents';
import CruiseMessageOverview from '../pages/dashboard/CruiseMessageOverview';
import CruiseManagement from '../pages/dashboard/CruiseManagement';
import CruiseBatchManagement from '../pages/dashboard/CruiseBatchManagement';
import CruiseSendingHistory from '../pages/dashboard/CruiseSendingHistory';
import CruiseTelementry from '../pages/dashboard/CruiseTelementry';
import PushNotification from '../pages/dashboard/PushNotification';
import PushNotificationCampaignDetails from '../pages/dashboard/PushNotificationCampaignDetails';
import GiftcardOrders from '../pages/dashboard/GiftcardOrders';
import ComplianceQueue from '../pages/dashboard/ComplianceQueue';
import ComplianceOverview from '../pages/dashboard/ComplianceOverview';
import ComplianceSystemHealth from '../pages/dashboard/ComplianceSystemHealth';
import ComplianceHistory from '../pages/dashboard/ComplianceHistory';
import UserList from '../pages/dashboard/UserList';
import UserProfilePage from '../pages/admin/UserProfilePage';
import UserWallet from '../pages/dashboard/UserWallet';
import SystemVariables from '../pages/dashboard/SystemVariables';
import ReferralRanking from '../pages/dashboard/ReferralRanking';
import MonthlyPendingBonus from '../pages/dashboard/MonthlyPendingBonus';
import ActivityLog from '../pages/dashboard/ActivityLog';

import Page500 from '../pages/Page500';
import NotFound from '../pages/Page404';
import TransactionHistory from 'pages/admin/TransactionHistory';
import BlockchainTransactionHistory from '../pages/admin/BlockchainTransactionHistory';
import InstantSwapLog from '../pages/admin/InstantSwapLog';
import BlockchainOpsPage from '../pages/admin/BlockchainOpsPage';
import AccessControlAdminListPage from '../pages/admin/AccessControlAdminListPage';
import AccessControlRequestsPage from '../pages/admin/AccessControlRequestsPage';
import AccessControlGrantsPage from '../pages/admin/AccessControlGrantsPage';
import AccessControlAuditPage from '../pages/admin/AccessControlAuditPage';
import RiskPolicySummaryPage from '../pages/admin/RiskPolicySummaryPage';
import RiskPolicyDecisionLogsPage from '../pages/admin/RiskPolicyDecisionLogsPage';
import RiskPolicyThresholdsPage from '../pages/admin/RiskPolicyThresholdsPage';
import RiskPolicySimulatorPage from '../pages/admin/RiskPolicySimulatorPage';
import QueueJobsOverviewPage from '../pages/admin/QueueJobsOverviewPage';
import QueueJobsListPage from '../pages/admin/QueueJobsListPage';
import QueueOutboxPage from '../pages/admin/QueueOutboxPage';
import ServiceReportsOverviewPage from '../pages/admin/ServiceReportsOverviewPage';
import ServiceReportsIncidentsPage from '../pages/admin/ServiceReportsIncidentsPage';
import SosAllSignalsPage from '../pages/admin/SosAllSignalsPage';
import SosOverviewPage from '../pages/admin/SosOverviewPage';
import AdjustmentsRequestsPage from '../pages/admin/AdjustmentsRequestsPage';
import AdjustmentsCreateRequestPage from '../pages/admin/AdjustmentsCreateRequestPage';
import ConfigGroupsPage from '../pages/admin/ConfigGroupsPage';
import ConfigFeatureFlagsPage from '../pages/admin/ConfigFeatureFlagsPage';
import ConfigAuditPage from '../pages/admin/ConfigAuditPage';

const withModuleGuard = (moduleName, element) => (
  <PermissionGuard moduleName={moduleName}>{element}</PermissionGuard>
);

export default function Router() {
  return useRoutes([
    {
      path: 'auth',
      children: [
        {
          path: 'login',
          element: (
            <GuestGuard>
              <Login />
            </GuestGuard>
          ),
        },
        {
          path: 'register',
          element: (
            <GuestGuard>
              <Register />
            </GuestGuard>
          ),
        },
        { path: 'login-unprotected', element: <Login /> },
        { path: 'register-unprotected', element: <Register /> },
        { path: 'reset-password', element: <ResetPassword /> },
      ],
    },
    {
      path: 'apps',
      element: (
        <AuthGuard>
          <AppSelector />
        </AuthGuard>
      ),
    },
    {
      path: 'giftcard',
      element: <Navigate to={PATH_GIFTCARD.orders} replace />,
    },
    {
      path: 'giftcard/orders',
      element: (
        <AuthGuard>
          <CardRedemptionApp />
        </AuthGuard>
      ),
    },
    {
      path: 'admin',
      element: (
        <AuthGuard>
          <DashboardLayout />
        </AuthGuard>
      ),
      children: [
        { index: true, element: <Navigate to={PATH_AFTER_LOGIN} replace /> },

        { path: 'dashboard', element: <AdminModulePage moduleKey="dashboard" /> },

        { path: 'access', element: withModuleGuard('access', <AccessControlAdminListPage />) },
        { path: 'access/staff', element: <Navigate to={PATH_DASHBOARD.accessControl.root} replace /> },
        { path: 'access/requests', element: withModuleGuard('access', <AccessControlRequestsPage />) },
        { path: 'access/grants', element: withModuleGuard('access', <AccessControlGrantsPage />) },
        { path: 'access/audit', element: withModuleGuard('access', <AccessControlAuditPage />) },

        { path: 'risk', element: <Navigate to={PATH_DASHBOARD.riskPolicy.summary} replace /> },
        { path: 'risk/summary', element: withModuleGuard('risk', <RiskPolicySummaryPage />) },
        { path: 'risk/decision-logs', element: withModuleGuard('risk', <RiskPolicyDecisionLogsPage />) },
        { path: 'risk/thresholds', element: withModuleGuard('risk', <RiskPolicyThresholdsPage />) },
        { path: 'risk/simulator', element: withModuleGuard('risk', <RiskPolicySimulatorPage />) },

        { path: 'users', element: <Navigate to={PATH_DASHBOARD.users.allUsers} replace /> },
        { path: 'users/all-users', element: <UserList /> },
        { path: 'users/all-users/:userId', element: <UserProfilePage /> },
        { path: 'users/wallet-balances', element: <UserWallet /> },
        // { path: 'users/controls', element: withModuleGuard('users', <AdminModulePage moduleKey="users" />) },
        { path: 'users/primary-account', element: withModuleGuard('users', <AdminModulePage moduleKey="users" />) },
        // { path: 'users/policy-decisions', element: withModuleGuard('users', <AdminModulePage moduleKey="users" />) },
        // { path: 'users/beneficiaries', element: withModuleGuard('users', <AdminModulePage moduleKey="users" />) },

        { path: 'transactions', element: <Navigate to={PATH_DASHBOARD.transactions.history} replace /> },
        { path: 'transactions/history', element: withModuleGuard('transactions', <TransactionHistory />) },
        { path: 'transactions/blockchain', element: withModuleGuard('transactions', <BlockchainTransactionHistory />) },
        { path: 'transactions/instant-swaps', element: withModuleGuard('transactions', <InstantSwapLog />) },
        { path: 'transactions/:transactionId', element: withModuleGuard('transactions', <AdminModulePage moduleKey="transactions" />) },

        // { path: 'settlement', element: <Navigate to={PATH_DASHBOARD.settlementOps.overview} replace /> },
        // { path: 'settlement/overview', element: withModuleGuard('settlement', <AdminModulePage moduleKey="settlementOps" />) },
        // { path: 'settlement/queue', element: withModuleGuard('settlement', <AdminModulePage moduleKey="settlementOps" />) },
        // { path: 'settlement/failed', element: withModuleGuard('settlement', <AdminModulePage moduleKey="settlementOps" />) },
        // { path: 'settlement/stalled', element: withModuleGuard('settlement', <AdminModulePage moduleKey="settlementOps" />) },
        // { path: 'settlement/recovery-log', element: withModuleGuard('settlement', <AdminModulePage moduleKey="settlementOps" />) },

        { path: 'blockchain', element: <Navigate to={PATH_DASHBOARD.blockchainOps.blockchainTx} replace /> },
        {
          path: 'blockchain/timeline/blockchain',
          element: withModuleGuard('services', <BlockchainOpsPage lookupMode="blockchain" />),
        },
        {
          path: 'blockchain/timeline/quote',
          element: withModuleGuard('services', <BlockchainOpsPage lookupMode="quote" />),
        },
        {
          path: 'blockchain/timeline/transaction',
          element: withModuleGuard('services', <BlockchainOpsPage lookupMode="transaction" />),
        },

        { path: 'queues', element: <Navigate to={PATH_DASHBOARD.queueJobs.overview} replace /> },
        { path: 'queues/overview', element: withModuleGuard('queues', <QueueJobsOverviewPage />) },
        { path: 'queues/queued', element: withModuleGuard('queues', <QueueJobsListPage />) },
        { path: 'queues/reserved', element: withModuleGuard('queues', <QueueJobsListPage />) },
        { path: 'queues/failed', element: withModuleGuard('queues', <QueueJobsListPage />) },
        { path: 'queues/outbox', element: withModuleGuard('queues', <QueueOutboxPage />) },

        { path: 'services', element: <Navigate to={PATH_DASHBOARD.serviceReports.overview} replace /> },
        { path: 'services/overview', element: withModuleGuard('services', <ServiceReportsOverviewPage />) },
        { path: 'services/incidents', element: withModuleGuard('services', <ServiceReportsIncidentsPage />) },
        { path: 'services/blockchain', element: withModuleGuard('services', <AdminModulePage moduleKey="serviceReports" />) },
        { path: 'services/provider/:provider', element: withModuleGuard('services', <AdminModulePage moduleKey="serviceReports" />) },

        { path: 'sos', element: <Navigate to={PATH_DASHBOARD.sos.overview} replace /> },
        { path: 'sos/overview', element: withModuleGuard('sos', <SosOverviewPage />) },
        { path: 'sos/signals', element: withModuleGuard('sos', <SosAllSignalsPage />) },

        { path: 'adjustments', element: <Navigate to={PATH_DASHBOARD.adjustments.list} replace /> },
        { path: 'adjustments/list', element: withModuleGuard('adjustments', <AdjustmentsRequestsPage />) },
        { path: 'adjustments/create', element: withModuleGuard('adjustments', <AdjustmentsCreateRequestPage />) },
        { path: 'adjustments/:requestId', element: withModuleGuard('adjustments', <AdjustmentsRequestsPage />) },

        { path: 'config', element: <Navigate to={PATH_DASHBOARD.config.groups} replace /> },
        { path: 'config/groups', element: withModuleGuard('config', <ConfigGroupsPage />) },
        { path: 'config/groups/:groupKey', element: withModuleGuard('config', <ConfigGroupsPage />) },
        { path: 'config/feature-flags', element: withModuleGuard('config', <ConfigFeatureFlagsPage />) },
        { path: 'config/audit', element: withModuleGuard('config', <ConfigAuditPage />) },

        { path: 'access-control', element: <Navigate to={PATH_DASHBOARD.accessControl.root} replace /> },
        { path: 'access-control/staff', element: <Navigate to={PATH_DASHBOARD.accessControl.staff} replace /> },
        { path: 'access-control/requests', element: <Navigate to={PATH_DASHBOARD.accessControl.requests} replace /> },
        { path: 'access-control/grants', element: <Navigate to={PATH_DASHBOARD.accessControl.grants} replace /> },
        { path: 'access-control/audit', element: <Navigate to={PATH_DASHBOARD.accessControl.audit} replace /> },

        { path: 'risk-policy', element: <Navigate to={PATH_DASHBOARD.riskPolicy.root} replace /> },
        { path: 'risk-policy/summary', element: <Navigate to={PATH_DASHBOARD.riskPolicy.summary} replace /> },
        { path: 'risk-policy/decision-logs', element: <Navigate to={PATH_DASHBOARD.riskPolicy.decisionLogs} replace /> },
        { path: 'risk-policy/thresholds', element: <Navigate to={PATH_DASHBOARD.riskPolicy.thresholds} replace /> },
        { path: 'risk-policy/simulator', element: <Navigate to={PATH_DASHBOARD.riskPolicy.simulator} replace /> },

        // { path: 'settlement-ops', element: <Navigate to={PATH_DASHBOARD.settlementOps.root} replace /> },
        // { path: 'settlement-ops/overview', element: <Navigate to={PATH_DASHBOARD.settlementOps.overview} replace /> },
        // { path: 'settlement-ops/queue', element: <Navigate to={PATH_DASHBOARD.settlementOps.queue} replace /> },
        // { path: 'settlement-ops/failed', element: <Navigate to={PATH_DASHBOARD.settlementOps.failed} replace /> },
        // { path: 'settlement-ops/stalled', element: <Navigate to={PATH_DASHBOARD.settlementOps.stalled} replace /> },
        // { path: 'settlement-ops/recovery-log', element: <Navigate to={PATH_DASHBOARD.settlementOps.recoveryLog} replace /> },

        { path: 'queue-jobs', element: <Navigate to={PATH_DASHBOARD.queueJobs.root} replace /> },
        { path: 'queue-jobs/overview', element: <Navigate to={PATH_DASHBOARD.queueJobs.overview} replace /> },
        { path: 'queue-jobs/queued', element: <Navigate to={PATH_DASHBOARD.queueJobs.queued} replace /> },
        { path: 'queue-jobs/reserved', element: <Navigate to={PATH_DASHBOARD.queueJobs.reserved} replace /> },
        { path: 'queue-jobs/failed', element: <Navigate to={PATH_DASHBOARD.queueJobs.failed} replace /> },
        { path: 'queue-jobs/outbox', element: <Navigate to={PATH_DASHBOARD.queueJobs.outbox} replace /> },

        { path: 'service-reports', element: <Navigate to={PATH_DASHBOARD.serviceReports.root} replace /> },
        { path: 'service-reports/overview', element: <Navigate to={PATH_DASHBOARD.serviceReports.overview} replace /> },
        { path: 'service-reports/incidents', element: <Navigate to={PATH_DASHBOARD.serviceReports.incidents} replace /> },
        { path: 'service-reports/blockchain', element: <Navigate to={PATH_DASHBOARD.serviceReports.blockchain} replace /> },
        { path: 'service-reports/provider/:provider', element: withModuleGuard('services', <AdminModulePage moduleKey="serviceReports" />) },

        { path: 'blockchain-ops', element: <Navigate to={PATH_DASHBOARD.blockchainOps.root} replace /> },
        { path: 'blockchain-ops/overview', element: <Navigate to={PATH_DASHBOARD.blockchainOps.blockchainTx} replace /> },
        { path: 'blockchain-ops/timeline/blockchain', element: <Navigate to={PATH_DASHBOARD.blockchainOps.blockchainTx} replace /> },
        { path: 'blockchain-ops/timeline/quote', element: <Navigate to={PATH_DASHBOARD.blockchainOps.quote} replace /> },
        {
          path: 'blockchain-ops/timeline/transaction',
          element: <Navigate to={PATH_DASHBOARD.blockchainOps.transaction} replace />,
        },

        { path: 'legacy', element: <Navigate to={PATH_DASHBOARD.legacy.adminManagement.list} replace /> },
        { path: 'legacy/admin-management', element: <Navigate to={PATH_DASHBOARD.legacy.adminManagement.list} replace /> },
        { path: 'legacy/admin-management/list', element: <AdminUserList /> },
        { path: 'legacy/crypto-assets', element: <Navigate to={PATH_DASHBOARD.legacy.cryptoAssets.list} replace /> },
        { path: 'legacy/crypto-assets/list', element: <CryptoList /> },
        { path: 'legacy/crypto-assets/ranges', element: <CryptoExchangeRange /> },
        { path: 'legacy/content', element: <Navigate to={PATH_DASHBOARD.legacy.content.featuredPosts} replace /> },
        { path: 'legacy/content/featured-posts', element: <FeaturedBlogPosts /> },
        { path: 'legacy/content/app-posts', element: <AppPostContents /> },
        { path: 'legacy/cruise-campaigns', element: <Navigate to={PATH_DASHBOARD.legacy.cruiseCampaigns.cruiseOverview} replace /> },
        { path: 'legacy/cruise-campaigns/cruise-overview', element: <CruiseMessageOverview /> },
        { path: 'legacy/cruise-campaigns/cruise-manage', element: <CruiseManagement /> },
        { path: 'legacy/cruise-campaigns/cruise-batch', element: <CruiseBatchManagement /> },
        { path: 'legacy/cruise-campaigns/cruise-history', element: <CruiseSendingHistory /> },
        { path: 'legacy/cruise-campaigns/cruise-telemetry', element: <CruiseTelementry /> },
        { path: 'legacy/cruise-campaigns/campaigns', element: <PushNotification /> },
        { path: 'legacy/cruise-campaigns/campaigns/:id/details', element: <PushNotificationCampaignDetails /> },
        { path: 'legacy/giftcard-operations', element: <Navigate to={PATH_DASHBOARD.legacy.giftcardOperations.giftcardOrders} replace /> },
        { path: 'legacy/giftcard-operations/orders', element: <GiftcardOrders /> },
        { path: 'legacy/compliance', element: <Navigate to={PATH_DASHBOARD.legacy.compliance.queue} replace /> },
        { path: 'legacy/compliance/queue', element: <ComplianceQueue /> },
        { path: 'legacy/compliance/overview', element: <ComplianceOverview /> },
        { path: 'legacy/compliance/health', element: <ComplianceSystemHealth /> },
        { path: 'legacy/compliance/history', element: <ComplianceHistory /> },
        { path: 'legacy/user-management', element: <Navigate to={PATH_DASHBOARD.legacy.userManagement.list} replace /> },
        { path: 'legacy/user-management/list', element: <UserList /> },
        { path: 'legacy/user-management/wallet', element: <UserWallet /> },
        { path: 'legacy/system-reports', element: <Navigate to={PATH_DASHBOARD.legacy.systemReports.variables} replace /> },
        { path: 'legacy/system-reports/variables', element: <SystemVariables /> },
        { path: 'legacy/system-reports/referral-ranking', element: <ReferralRanking /> },
        { path: 'legacy/system-reports/monthly-bonus', element: <MonthlyPendingBonus /> },
        { path: 'legacy/system-reports/activities', element: <ActivityLog /> },
        { path: 'legacy/blockchain-views', element: <AdminModulePage moduleKey="legacyBlockchainViews" /> },
        { path: 'legacy/blockchain-views/quidax-accounts', element: <AdminModulePage moduleKey="legacyBlockchainViews" /> },
        { path: 'legacy/blockchain-views/wallet-addresses', element: <AdminModulePage moduleKey="legacyBlockchainViews" /> },
        { path: 'legacy/blockchain-views/instant-swaps', element: <AdminModulePage moduleKey="legacyBlockchainViews" /> },
      ],
    },
    {
      path: PATH_PAGE.unauthorized,
      element: <Unauthorized />,
    },
    {
      path: '*',
      element: <LogoOnlyLayout />,
      children: [
        { path: '500', element: <Page500 /> },
        { path: '404', element: <NotFound /> },
        { path: '*', element: <Navigate to="/404" replace /> },
      ],
    },
    {
      path: '/',
      element: (
        <GuestGuard>
          <Login />
        </GuestGuard>
      ),
    },
    { path: '*', element: <Navigate to="/404" replace /> },
  ]);
}
